import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../../redux/actions';
import CloseIcon from '@mui/icons-material/Close';

const MARKERS = {
  added: '+',
  changed: '•',
  removed: '−',
};

const MILESTONES = {
  created: 'Created',
  submitted: 'Submitted',
  resent: 'Resent',
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'after_submit', label: 'After submission' },
];

const formatTime = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const formatWhen = (iso) => new Date(iso).toLocaleString('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const dayKey = (iso) => new Date(iso).toLocaleDateString('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const Value = ({ value, removed }) => (
  value
    ? <span className={`report-change-value${removed ? ' removed' : ''}`}>{value}</span>
    : <span className="report-change-value empty">blank</span>
);

const Milestone = ({ change }) => (
  <li className={`report-change-milestone report-change-milestone--${change.action}`}>
    <span className="report-change-milestone-label">{MILESTONES[change.action]}</span>
    <span className="report-change-milestone-detail">
      {change.user_name || 'Unknown user'} · {formatTime(change.updated_at)}
      {change.new_value ? ` · ${change.action === 'created' ? change.new_value : `to ${change.new_value}`}` : ''}
    </span>
  </li>
);

const ChangeRow = ({ change }) => {
  const [expanded, setExpanded] = useState(false);
  const coalesced = change.updated_at !== change.created_at;
  const who = change.user_name || 'Unknown user';
  const title = coalesced
    ? `${who} · ${formatWhen(change.created_at)} – ${formatWhen(change.updated_at)}`
    : `${who} · ${formatWhen(change.updated_at)}`;

  return (
    <li
      className={`report-change report-change--${change.action}${expanded ? ' expanded' : ''}`}
      onClick={() => setExpanded(!expanded)}
      title={expanded ? undefined : title}
      aria-expanded={expanded}
    >
      <time className="report-change-time">{formatTime(change.updated_at)}</time>
      <span className="report-change-marker" aria-label={change.action}>{MARKERS[change.action] || '•'}</span>
      <span className="report-change-text">
        <span className="report-change-label">{change.label}</span>
        {change.action === 'changed' && (
          <><Value value={change.old_value} /><span className="report-change-arrow" aria-label="to">→</span><Value value={change.new_value} /></>
        )}
        {change.action === 'added' && change.new_value && <Value value={change.new_value} />}
        {change.action === 'removed' && change.old_value && <Value value={change.old_value} removed />}
      </span>
      {expanded && <span className="report-change-detail">{title}</span>}
    </li>
  );
};

const ReportChanges = ({ reportId, authToken, fetchReportChanges, close }) => {
  const [changes, setChanges] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchReportChanges(reportId, authToken)
      .then((json) => {
        setChanges(json.changes || []);
        setSubmitted(json.submitted);
      })
      .catch((e) => setError(e.message));
  }, [reportId, authToken, fetchReportChanges]);

  const visible = (changes || []).filter((c) => filter === 'all' || c.after_submit);
  const groups = visible.reduce((acc, change) => {
    const key = dayKey(change.updated_at);
    const group = acc.find((g) => g.key === key);
    if (group) group.items.push(change);
    else acc.push({ key, items: [change] });
    return acc;
  }, []);

  return (
    <div className="report-modal" onClick={close}>
      <div className="report-changes-container" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Report history">
        <header className="report-changes-header">
          <div>
            <h2 className="report-changes-title">Report history</h2>
            <p className="report-changes-subtitle">
              {submitted ? `Submitted ${formatWhen(submitted)}` : 'Not submitted yet'} · Visible to ProAsys staff only
            </p>
          </div>
          <button type="button" className="report-changes-close" onClick={close} aria-label="Close">
            <CloseIcon />
          </button>
        </header>

        {submitted && (
          <div className="report-changes-filters" role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                className={`report-changes-filter${filter === f.id ? ' active' : ''}`}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {error && <p className="report-changes-empty">{error}</p>}
        {!error && !changes && <p className="report-changes-empty">Loading…</p>}
        {changes && visible.length === 0 && (
          <p className="report-changes-empty">
            {filter === 'after_submit' ? 'No changes since this report was submitted.' : 'No history recorded yet.'}
          </p>
        )}

        {groups.map((group) => (
          <section key={group.key} className="report-changes-day">
            <h3 className="report-changes-day-label">{group.key}</h3>
            <ul className="report-changes-list">
              {group.items.map((change) => (
                MILESTONES[change.action]
                  ? <Milestone key={change.id} change={change} />
                  : <ChangeRow key={change.id} change={change} />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ReportChanges);
